export const OVERFLOW_THRESHOLD_PX = 4;
export const COMPRESSED_ON_PX = 6;
export const COMPRESSED_OFF_PX = 2;

export const FIRST_COLUMN_MIN_PX = 150;
export const OTHER_COLUMN_MIN_PX = 125;

export type LayoutMode = 'fill' | 'scroll';

// Row budget from measured container width (parent layout defines the cap)
export function getFillBudget(containerWidth: number): number {
	if (containerWidth <= 0) {
		return 0;
	}
	return containerWidth;
}

export function getMinimumTableWidth(columnCount: number): number {
	if (columnCount <= 0) {
		return 0;
	}
	return FIRST_COLUMN_MIN_PX + (columnCount - 1) * OTHER_COLUMN_MIN_PX;
}

function sum(values: number[]): number {
	return values.reduce((total, value) => total + value, 0);
}

export function distributeExactTotal(total: number, count: number): number[] {
	if (count <= 0) {
		return [];
	}

	// Split the total into equal parts
	const base = Math.floor(total / count);
	const widths = Array.from({ length: count }, () => base);
	let remainder = total - base * count;

	// Distribute the remainder to the trailing columns
	for (let index = count - 1; index >= 0 && remainder > 0; index -= 1) {
		widths[index] += 1;
		remainder -= 1;
	}

	return widths;
}

export function getColumnMinWidth(columnIndex: number): number {
	return columnIndex === 0 ? FIRST_COLUMN_MIN_PX : OTHER_COLUMN_MIN_PX;
}

export function getContentBaseWidths(naturalWidths: number[]): number[] {
	return naturalWidths.map((natural, index) => Math.max(natural || 0, getColumnMinWidth(index)));
}

export function canFillAtWidth(layoutWidth: number, columnCount: number): boolean {
	// If the column count is less than 0 or the layout width is less than 0, return false
	if (columnCount <= 0 || layoutWidth <= 0) {
		return false;
	}
	return getMinimumTableWidth(columnCount) <= layoutWidth;
}

export function requiresScrollByMinimums(layoutWidth: number, columnCount: number): boolean {
	if (columnCount === 0 || layoutWidth <= 0) {
		return false;
	}
	// Return true if the column count is less than 0 or the layout width is less than 0
	return !canFillAtWidth(layoutWidth, columnCount);
}

export function resolveLayoutMode(layoutWidth: number, columnCount: number): LayoutMode {
	if (requiresScrollByMinimums(layoutWidth, columnCount)) {
		return 'scroll';
	}
	return 'fill';
}

export function computeFillColumnWidths(containerWidth: number, naturalWidths: number[]): number[] {
	const columnCount = naturalWidths.length;
	if (columnCount <= 0 || containerWidth <= 0) {
		return [];
	}

	const budget = getFillBudget(containerWidth);
	if (!canFillAtWidth(budget, columnCount)) {
		return [];
	}

	const baseWidths = getContentBaseWidths(naturalWidths);
	const equalShare = Math.floor(budget / columnCount);

	// If the equal share is less than the first column minimum width, return the first column minimum width and distribute the remainder
	if (equalShare < FIRST_COLUMN_MIN_PX) {
		const otherCount = columnCount - 1;
		if (otherCount === 0) {
			return [FIRST_COLUMN_MIN_PX];
		}
		return [FIRST_COLUMN_MIN_PX, ...distributeExactTotal(budget - FIRST_COLUMN_MIN_PX, otherCount)];
	}

	const baseTotal = sum(baseWidths);
	const surplus = budget - baseTotal;
	// Calculate the surplus per column
	const surplusPerColumn = surplus / columnCount;
	const maxBase = Math.max(...baseWidths);

	// If the surplus is less than 0 or the surplus per column is less than the maximum base width, return the distribute exact total
	// Calculate the maximum base width
	if (surplus <= 0 || surplusPerColumn < maxBase) {
		return distributeExactTotal(budget, columnCount);
	}

	return baseWidths;
}

export function computeScrollColumnWidths(layoutWidth: number, naturalWidths: number[]): number[] {
	const columnCount = naturalWidths.length;
	// Return an empty array if the column count is 0 or the layout width is less than 0
	if (columnCount === 0 || layoutWidth <= 0) {
		return [];
	}

	// Calculate the maximum width for the first column
	const col0Max = Math.floor(layoutWidth / 3);
	// Get the natural width for the first column
	const col0Natural = naturalWidths[0] ?? 0;
	let col0 = FIRST_COLUMN_MIN_PX;
	// If the maximum width for the first column is greater than the first column minimum width, set the first column width to the minimum of the natural width and the maximum width
	if (col0Max >= FIRST_COLUMN_MIN_PX) {
		col0 = Math.min(Math.max(col0Natural, FIRST_COLUMN_MIN_PX), col0Max);
	}

	// If the column count is 1, return the first column width
	if (columnCount === 1) {
		return [col0];
	}

	// Calculate the remaining visible table width
	let remainingVisibleTableWidth = layoutWidth - col0;
	// If the remaining visible table width is less than the first column width, set the first column width to the first column minimum width and the remaining visible table width to the layout width minus the first column width
	if (remainingVisibleTableWidth * 2 < col0) {
		col0 = FIRST_COLUMN_MIN_PX;
		remainingVisibleTableWidth = layoutWidth - col0;
	}

	// Apply the first column minimum width to the natural widths
	const clampedNaturals = naturalWidths.map((natural, index) =>
		index === 0 ? col0 : Math.max(natural, OTHER_COLUMN_MIN_PX)
	);
	// Calculate the full table minimum width by cells
	const fullTableMinWidthByCells = Math.min(sum(clampedNaturals), layoutWidth);
	// Calculate the full budget
	const fullBudget = fullTableMinWidthByCells - clampedNaturals[0];
	// Calculate the maximum width even
	const maxWidthEven = Math.floor(fullBudget / (columnCount - 1));

	const widths = naturalWidths.map((natural, index) => {
		// If the index is 0, return the first column width
		if (index === 0) {
			return col0;
		}

		// Calculate the width to use
		let widthToUse = Math.max(OTHER_COLUMN_MIN_PX, Math.min(natural || 0, maxWidthEven));

		// If the natural content exceeds the allocated width, shrink to the visible remainder
		if ((natural || 0) > widthToUse && remainingVisibleTableWidth < widthToUse) {
			widthToUse = Math.max(OTHER_COLUMN_MIN_PX, remainingVisibleTableWidth);
		}

		return widthToUse;
	});

	return widths;
}

export interface ColumnLayoutResult {
	mode: LayoutMode;
	widths: number[];
}

export function computeColumnLayout(
	layoutWidth: number,
	naturalWidths: number[]
): ColumnLayoutResult {
	const columnCount = naturalWidths.length;
	const mode = resolveLayoutMode(layoutWidth, columnCount);

	if (mode === 'scroll') {
		return { mode, widths: computeScrollColumnWidths(layoutWidth, naturalWidths) };
	}

	const widths = computeFillColumnWidths(layoutWidth, naturalWidths);
	if (widths.length === 0) {
		return { mode: 'scroll', widths: computeScrollColumnWidths(layoutWidth, naturalWidths) };
	}

	return { mode: 'fill', widths };
}
